/**
 * DataGrid Component
 * Reusable sortable data grid with column configuration
 */

import React, { useState } from 'react';
import { theme } from '../../theme';
import { Table, TableHeader, TableBody, TableRow, TableCell } from './Table';
import { SortableHeaderCell, SortDirection } from './SortableHeaderCell';

export type { SortDirection };

export interface DataGridColumn<T> {
  key: string;
  /**
   * Header content. String is the common case; ReactNode is supported
   * so callers can render a select-all checkbox or other non-text
   * affordance inside the header cell (e.g. bulk-select column).
   */
  header?: React.ReactNode;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  render: (item: T) => React.ReactNode;
}

export interface DataGridProps<T> {
  columns: DataGridColumn<T>[];
  data: T[];
  onSort?: (key: string, direction: SortDirection) => void;
  onRowClick?: (item: T) => void;
  rowStyle?: (item: T, index: number) => React.CSSProperties | undefined;
  style?: React.CSSProperties;
  emptyMessage?: string;
  currentSortKey?: string | null;
  currentSortDirection?: SortDirection;
  /**
   * Cycle order used by sortable headers. Defaults to
   * `['asc', 'desc', null]` (click cycles asc → desc → clear). Pass
   * `['asc', 'desc']` to lock to a two-state toggle when a clear-sort
   * state is meaningless for the grid (e.g. server-paginated tables
   * that always need a sort column).
   */
  cycle?: ('asc' | 'desc' | null)[];
  /**
   * Alternating row banding (odd rows get a subtle quaternary background),
   * matching the canonical ServerSideTable's zebra. Opt-in so existing
   * DataGrid consumers (work-order lists, invoice tables) are unchanged; the
   * report grids enable it for cross-report consistency. A caller `rowStyle`
   * background still wins (it merges over the band).
   */
  zebra?: boolean;
  /**
   * Minimum table width. When the container is narrower, the grid scrolls
   * horizontally instead of squeezing columns — use it on wide report grids so
   * they stay readable on small screens.
   */
  minWidth?: number | string;
}

/**
 * Reusable DataGrid component with sorting
 */
function DataGridInner<T>({
  columns,
  data,
  onSort,
  onRowClick,
  rowStyle,
  style,
  emptyMessage = 'No data available',
  currentSortKey,
  currentSortDirection,
  cycle,
  zebra,
  minWidth,
}: DataGridProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  const activeSortKey = currentSortKey !== undefined ? currentSortKey : sortKey;
  const activeSortDirection = currentSortDirection !== undefined ? currentSortDirection : sortDirection;

  const handleSort = (key: string, direction: SortDirection) => {
    if (currentSortKey === undefined) {
      setSortKey(direction ? key : null);
      setSortDirection(direction);
    }
    onSort?.(key, direction);
  };

  return (
    <Table style={style} minWidth={minWidth}>
      <TableHeader>
        {columns.map((column) => (
          <SortableHeaderCell
            key={column.key}
            sortKey={column.key}
            label={column.header}
            sortable={column.sortable}
            activeKey={activeSortKey}
            activeDirection={activeSortDirection}
            onSort={handleSort}
            cycle={cycle}
            width={column.width}
            align={column.align}
          />
        ))}
      </TableHeader>
      <TableBody>
        {data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length} align="center" style={{ padding: '32px', color: theme.colors.text.secondary }}>
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          data.map((item, index) => {
            const customStyle = rowStyle?.(item, index);
            // Zebra band for odd rows, matching ServerSideTable; a caller
            // rowStyle background still wins because customStyle spreads last.
            const bandBg = zebra && index % 2 === 1 ? theme.colors.background.quaternary : undefined;
            const defaultBg = customStyle?.backgroundColor || bandBg || 'transparent';
            return (
              <TableRow
                key={index}
                onClick={() => onRowClick?.(item)}
                style={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'background-color 0.2s ease',
                  ...(bandBg ? { backgroundColor: bandBg } : {}),
                  ...customStyle,
                }}
                onMouseEnter={(e) => {
                  if (onRowClick) {
                    e.currentTarget.style.backgroundColor = theme.colors.background.tertiary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (onRowClick) {
                    e.currentTarget.style.backgroundColor = defaultBg;
                  }
                }}
              >
                {columns.map((column) => (
                  <TableCell key={column.key} align={column.align} width={column.width}>
                    {column.render(item)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}

/**
 * Memoized export. DataGrid renders O(rows × columns) cells, so skipping a
 * render when props are reference-equal is a real win for large grids
 * (WorkOrders renders up to 100 rows × ~12 columns).
 *
 * Memo only pays off when the consumer keeps props stable:
 *  - `columns` must be `useMemo`'d (the `render` functions live inside it).
 *  - callbacks (`onSort`, `onRowClick`, `rowStyle`) must be `useCallback`'d.
 * Consumers that pass inline props simply fall back to re-rendering every
 * time — identical to the pre-memo behavior, never incorrect, because
 * DataGrid is a pure function of its props plus its own internal sort state.
 *
 * `React.memo` erases generics, so cast back to the inner component's type
 * to keep `<DataGrid<MyRow> …>` call sites type-checking.
 */
export const DataGrid = React.memo(DataGridInner) as typeof DataGridInner;

export default DataGrid;
